import { AbsoluteCenter, Box, Text } from '@chakra-ui/react';
import { ReplayPlayer } from '@components/common/video/ReplayPlayer';
import { useAppSelector } from '@hooks/redux.hooks';
import { usePrevious } from '@hooks/usePrevious';
import { selectReplaySpeed } from '@redux/replay/replay.selectors';
import { PerimeterCameraSelectors } from '@redux/sensors/sensors.selectors';
import { selectSituationTime } from '@redux/situation/situation.selectors';
import { useUpdatePerimeterCameraConfigurationMutation } from '@services/c2/c2.api';
import { formatDate } from '@utils/date.utils';
import { addDateToSourceReplayUrl, isValidUrl } from '@utils/replay/replay.utils';
import { isEqual } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { DateTimeEnum } from '@/types/dateTime.types';
import { PerimeterCamera } from '@/types/sensor/sensors/perimeterCamera.types';

import CameraBorder from './CameraBorder';
import CameraControlTitle from './CameraControlTitle';

interface OwnProps {
  selectedPerimeterCameraUniqueKey: string | null;
  playerId: string;
}

function CameraPerimReplay({ selectedPerimeterCameraUniqueKey, playerId }: Readonly<OwnProps>) {
  const perimeterCamera: PerimeterCamera | undefined = useAppSelector(
    (state) => PerimeterCameraSelectors.selectPerimeterCameraByUniqueKey(state, selectedPerimeterCameraUniqueKey ?? ''),
    isEqual,
  );
  const [updatePericam] = useUpdatePerimeterCameraConfigurationMutation();

  const replayDateRaw: string | null = useAppSelector((state) => selectSituationTime(state, true));
  const replayDate: string | null = replayDateRaw
    ? formatDate(new Date(replayDateRaw), DateTimeEnum.DATE_TIME_SECONDS_PERIM_FORMAT)
    : null;
  const replaySpeed = useAppSelector(selectReplaySpeed);

  const isPaused = replaySpeed === 0;
  const prevIsPaused = usePrevious(isPaused, isPaused);
  const prevCameraCode = usePrevious(selectedPerimeterCameraUniqueKey);
  const prevReplayDate = usePrevious(replayDate);

  // Quand la frise est en lecture replayDate avance chaque seconde. lastTimeStamp est utilisé pour éviter qu'un PUT
  // parte toutes les secondes
  const [lastTimeStamp, setLastTimeStamp] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (
      replayDate &&
      (prevCameraCode !== selectedPerimeterCameraUniqueKey ||
        prevReplayDate !== replayDate ||
        (prevIsPaused && replaySpeed !== 0)) &&
      perimeterCamera &&
      isValidUrl(perimeterCamera.sourceReplayUrl)
    ) {
      let tmpLastTimeStamp = lastTimeStamp;
      if (lastTimeStamp || (replayDate && prevReplayDate)) {
        tmpLastTimeStamp = Math.abs(new Date(replayDate).getTime() - new Date(prevReplayDate ?? 0).getTime());
        setLastTimeStamp(tmpLastTimeStamp);
      }

      // 0.2 sec added due to latency
      if ((tmpLastTimeStamp && tmpLastTimeStamp > 1200) || (prevIsPaused && replaySpeed !== 0)) {
        updatePericam({
          id: perimeterCamera.id,
          site: perimeterCamera.site,
          pericam: {
            ...perimeterCamera,
            sourceReplayUrl: addDateToSourceReplayUrl(perimeterCamera.sourceReplayUrl!, replayDate),
          },
        });
      }
    }
  }, [
    perimeterCamera,
    prevCameraCode,
    prevReplayDate,
    replayDate,
    selectedPerimeterCameraUniqueKey,
    updatePericam,
    replaySpeed,
    lastTimeStamp,
    prevIsPaused,
  ]);

  const displayPlayer = useCallback(() => {
    if (selectedPerimeterCameraUniqueKey && perimeterCamera && replayDateRaw) {
      return (
        <Box width="100%" height="100%">
          <ReplayPlayer
            id={playerId}
            src={perimeterCamera.replayUrl}
            playerType={perimeterCamera.playerType}
            isPaused={replaySpeed === 0}
          />
        </Box>
      );
    }

    const statusMessage = !replayDateRaw ? 'cameras.noReplayTime' : 'cameras.noFlux';

    return (
      <AbsoluteCenter>
        <Text>
          <FormattedMessage id={!selectedPerimeterCameraUniqueKey ? 'cameras.noSelectedCamera' : statusMessage} />
        </Text>
      </AbsoluteCenter>
    );
  }, [selectedPerimeterCameraUniqueKey, perimeterCamera, playerId, replayDateRaw, replaySpeed]);

  return (
    <>
      <CameraBorder hasControl={false} />
      <CameraControlTitle replayMode />
      {displayPlayer()}
    </>
  );
}

export default CameraPerimReplay;
